import { type Boundary } from '@floating-ui/react-dom'
import { Slot } from '@radix-ui/react-slot'
import { useControllableState } from '@radix-ui/react-use-controllable-state'
import React, { forwardRef, useContext, useEffect, useId, useLayoutEffect, useRef } from 'react'

import { useTimeout } from 'slab/hooks/useTimeout'
import { type IconProp } from 'slab/types'

import { Popper } from '../Popper'
import { Presence } from '../Presence'
import { TooltipArrow } from './Arrow'
import { TooltipContainer } from './Container'
import { TooltipContext } from './context'
import { useTooltipState } from './useTooltipState'

interface TooltipProps {
  align?: 'start' | 'center' | 'end';
  arrowAlign?: 'start' | 'center' | 'end';
  boundary?: Boundary;
  children?: React.ReactNode;
  closeOnClick?: boolean;
  collisionPadding?: number;
  content: React.ReactNode;
  defaultOpen?: boolean;
  /**
   * The delay duration in milliseconds before the tooltip is shown.
   */
  delayDuration?: number;
  /**
   * Use this prop to conditionally enable the tooltip. This is more ergonomic
   * than conditionally wrapping the trigger in a `<Tooltip>` component.
   */
  enabled?: boolean;
  /**
   * The duration in milliseconds before the tooltip is closed when the mouse
   * leaves the trigger. This defines the time the user has to seamlessly move
   * to another tooltip trigger before the current tooltip animates out.
   */
  gracePeriod?: number;
  icon?: IconProp;
  onOpenChange?: (open: boolean) => void;
  open?: boolean;
  /**
   * Controls whether the tooltip should receive pointer events. Pointer events
   * are allowed by default.
   *
   * In most cases the tooltip _should_ receive pointer events so it can be
   * interacted with without closing. But if a tooltip is following a user's
   * cursor, for example, it should probably not receive pointer events
   * so that it doesn't interrupt event listeners that are tracking the user's
   * mouse movement.
   */
  pointerEvents?: 'auto' | 'none';
  portalContainer?: Element;
  side?: 'top' | 'right' | 'bottom' | 'left';
  virtualAnchor?: DOMRect | null;
  withPortal?: boolean;
  zIndex?: number;
}

const MaybePortal = ({
  withPortal,
  portalContainer,
  children,
}: {
  withPortal: boolean;
  portalContainer?: Element;
  children: React.ReactNode;
}) => {
  if (withPortal) {
    return (
      <Popper.Portal portalContainer={portalContainer}>
        {children}
      </Popper.Portal>
    )
  }
  return children
}

export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(({
  align = 'center',
  arrowAlign = 'center',
  boundary,
  children,
  closeOnClick = true,
  collisionPadding = 0,
  content,
  defaultOpen = false,
  delayDuration: delayDurationProp,
  enabled = true,
  gracePeriod: gracePeriodProp,
  icon: Icon,
  onOpenChange,
  open: openProp,
  portalContainer,
  pointerEvents,
  side = 'top',
  virtualAnchor,
  withPortal = true,
  zIndex,
  ...props
}: TooltipProps, forwardedRef) => {
  const [open, setOpen] = useControllableState({
    defaultProp: defaultOpen,
    prop: openProp,
    onChange: onOpenChange,
  })
  const tooltipId = useId()
  const { delayDuration, gracePeriod, onOpen, onClose, wasAlreadyOpenRef } = useContext(TooltipContext)
  const otherTooltipIsOpen = useTooltipState(({ openTooltipId }) => (
    !!openTooltipId && openTooltipId !== tooltipId
  ))

  const closeTimeout = useTimeout()
  const openTimeout = useTimeout()
  const wasDismissedWhileHovered = useRef(false)

  const isExternallyControlled = openProp !== undefined

  const openDelay = otherTooltipIsOpen ? 0 : delayDurationProp ?? delayDuration
  const gracePeriodDelay = gracePeriodProp || gracePeriod

  function openTooltip () {
    /**
     * If the tooltip is dismissed while the user's mouse is still over the
     * trigger (e.g. if the user clicks the trigger) we don't want the tooltip
     * to open again until the user has moved their mouse away from the trigger.
     * Otherwise the tooltip will reappear as soon as they move their mouse.
     */
    if (wasDismissedWhileHovered.current) return

    setOpen(true)
    onOpen(tooltipId)
  }

  function closeTooltip () {
    setOpen(false)
    onClose(tooltipId)
  }

  /** Closes the tooltip immediately if another tooltip has been triggered. */
  useLayoutEffect(() => {
    if (enabled && open && otherTooltipIsOpen) {
      closeTooltip()
    }
  }, [enabled, open, otherTooltipIsOpen])

  useEffect(() => {
    if (enabled && open && !isExternallyControlled) {
      function handleScroll () {
        closeTooltip()
      }

      window.addEventListener('scroll', handleScroll, true)

      return () => {
        window.removeEventListener('scroll', handleScroll, true)
      }
    }
  }, [enabled, open, isExternallyControlled])

  if (!enabled) return children

  function handleAnimationStart (event: React.AnimationEvent<HTMLDivElement>) {
    const target = event.target as HTMLDivElement

    /**
     * Skip the animation if another tooltip was already open to provide a more
     * seamless experience while moving between tooltip triggers.
     */
    if (wasAlreadyOpenRef.current) {
      const animations = target.getAnimations()
      animations.forEach((animation) => animation.finish())
    }
  }

  function handleLeave (skipGracePeriod?: boolean) {
    if (isExternallyControlled) return

    openTimeout.clear()

    if (wasDismissedWhileHovered.current) {
      wasDismissedWhileHovered.current = false
      return
    }

    if (open) {
      // Even when skipping the grace period, we still close the tooltip after
      // a small delay (one tick of the event loop) to leave time for the
      // `handleEnter` handler of another tooltip trigger to run first. This
      // ensures smooth transitions between multiple tooltip triggers.
      closeTimeout.clear()
      closeTimeout.set(closeTooltip, skipGracePeriod ? 0 : gracePeriodDelay)
    }
  }

  function handleEnter (skipDelay?: boolean) {
    if (isExternallyControlled) return

    closeTimeout.clear()

    if (skipDelay) {
      openTooltip()
    } else {
      openTimeout.clear()
      openTimeout.set(openTooltip, openDelay)
    }
  }

  /**
   * This handler is attached to the `click` event but is really only handling
   * the case where a button is activated by keyboard. "Clicks" from a pointer
   * (e.g. a mouse click or tap) are handled by `handlePointerDown`.
   */
  function handleClick (event: React.PointerEvent<HTMLButtonElement>) {
    if (isExternallyControlled || closeOnClick === false) return

    /**
     * Clicks triggered by a mouse or touch screen will have a `pointerType`
     * property. Clicks triggered by keyboard will not.
     */
    if (!event.nativeEvent.pointerType) {
      openTimeout.clear()

      if (open) {
        closeTooltip()
      }
    }
  }

  /**
   * We're attaching this handler to the `pointerdown` event instead of `click`
   * to provide more immediate feedback to mouse and touch screen users. This
   * is especially important for mobile users, who otherwise wouldn't see
   * the tooltip until after they had lifted their finger.
   */
  function handlePointerDown () {
    openTimeout.clear()
    closeTimeout.clear()

    if (open && closeOnClick !== false) {
      closeTooltip()
      wasDismissedWhileHovered.current = true
    } else {
      openTooltip()
    }
  }

  function renderAnchor () {
    if (virtualAnchor) {
      return (
        <Popper.Anchor virtualAnchor={virtualAnchor} />
      )
    }

    return (
      <Popper.Anchor
        aria-describedby={tooltipId}
        asChild
        data-state={open ? 'open' : 'closed'}
        onBlur={() => handleLeave(true)}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onFocus={() => handleEnter(true)}
        onMouseEnter={() => handleEnter(false)}
        onMouseLeave={() => handleLeave(false)}
        tabIndex={0}
      >
        <Slot {...props} ref={forwardedRef}>
          {children}
        </Slot>
      </Popper.Anchor>
    )
  }

  return (
    <Popper>
      {renderAnchor()}
      <MaybePortal withPortal={withPortal} portalContainer={portalContainer}>
        <Presence asChild present={open}>
          <Popper.Content
            asChild
            align={align}
            arrowAlign={arrowAlign}
            arrowPadding={8}
            boundary={boundary}
            collisionPadding={collisionPadding}
            id={tooltipId}
            onBlur={() => handleLeave(true)}
            onFocus={() => handleEnter(true)}
            onMouseEnter={() => {
              if (open) handleEnter(false)
            }}
            onMouseLeave={() => {
              handleLeave(false)
            }}
            pointerEvents={pointerEvents}
            role='tooltip'
            side={side}
            sideOffset={0}
            zIndex={zIndex}
          >
            <TooltipContainer icon={Icon} open={open} onAnimationStart={handleAnimationStart}>
              {content}
              <Popper.Arrow>
                {({ side }) => <TooltipArrow side={side} />}
              </Popper.Arrow>
            </TooltipContainer>
          </Popper.Content>
        </Presence>
      </MaybePortal>
    </Popper>
  )
})
